import React, { useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextRef } from './authContextRef';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  TotpMultiFactorGenerator,
  signOut,
  reload,
  User,
  type MultiFactorResolver,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { sendEmailConfirmation, type EmailConfirmationOutcome } from '@/core/apiProxy';
import { signUpWithEmail } from '@/core/publicApi';
import i18n from '@/i18n/config';
import { clearAllLocalDrafts, scrubStoredAuthTokens } from '@/utils/draftStorage';

export interface LearnerProfile {
  uid: string;
  preferredName?: string;
  /** written once by ProfileSetupPage (Timestamp); only its presence is read (firestore.rules lets it be written once) */
  createdAt?: unknown;
  /** Round 132b - "I am 18 or older", a server-stamped Timestamp written once (firestore.rules); only its presence is read */
  ageConfirmedAt?: unknown;
  firstName?: string;
  lastName?: string;
  subjectArea?: string;
  learningGoal?: string;
  placementLevel?: string;
  unlockedLessonIds?: string[];
  subscriptionStatus?: 'trial' | 'active' | 'expired' | 'cancelled' | 'revoked';
  trialLessonsRemaining?: number;
  /** Round 124 - lessons done in the current CEFR block (the block plan's counter; 120 per block) */
  blockLessonsDone?: number;
  /** Round 128 - the block level being worked and the block attempt (absent = 1); both written by the progression transaction */
  currentBlock?: string;
  blockAttempt?: number;
  streakCount?: number;
  currentStreak?: number;
  lessonsCompletedThisWeek?: number;
  lessonsCompleted?: number;
  lastLessonDate?: string | null;
  lastActiveAt?: any;
  placementLocked?: {
    reading?: string;
    listening?: string;
    writing?: string;
    speaking?: string;
  };
  // Round 151 (B2) - server-written: the team asked the learner to redo the placement test for these skills (pending = not redone yet)
  placementRedo?: { skills?: string[]; pending?: string[]; requestedAt?: string } | null;
  currentLevel?: {
    reading?: string;
    listening?: string;
    writing?: string;
    speaking?: string;
  };
  // Round 31, B3 — re-surfacing signal only (sessionId/lessonId, no
  // weakPhases — that's a separate fetch, apiProxy.getPendingRetakeOffer,
  // since the full offer detail lives on the session doc, not the
  // profile). This field was already reaching the client via the raw
  // getDoc read below; it just wasn't in this interface yet.
  pendingRetakeOffer?: { sessionId: string; lessonId: string } | null;
  // Round 33, E1/E3 — the terminal state. Same "already reaching the
  // client via the raw getDoc read, just not typed yet" situation as
  // pendingRetakeOffer above.
  courseCompletedAt?: string;
  // Round 53, B5 — same "already reaching the client via the raw getDoc
  // read" situation. Only `status` is read by LessonHomePage; the rest is
  // typed for completeness/future admin-side reuse, not consumed here.
  progressReview?: {
    status: 'flagged' | 'resolved';
    flaggedAt: string;
    flaggedSkills: string[];
    flaggedAtBlock: string;
    resolvedAt?: string;
    resolvedBy?: string;
    resolutionNote?: string;
  };
  // Round 63, A4 — written by ProfileSetupPage.tsx's setDoc; typed here so
  // they don't join the "computed and discarded" pattern CLAUDE.md tracks
  // (a real written field with no frontend type, so nothing can ever read
  // it back). Not consumed anywhere in this app yet — that's Round 63, B's
  // separate, not-yet-built analytics question.
  countryAtSignup?: string | null;
  detectedCountry?: string | null;
  timezone?: string | null;
  // Collected at SlickPay checkout (SubscribePage.tsx), not at signup, and
  // written back to the profile by the backend the first time it is given.
  // Older profiles may carry one from the removed signup-time field. Read by
  // SubscribePage.tsx to decide whether the checkout phone prompt is needed.
  phone?: string | null;
  // Phase G, G6 — learner's chosen UI language (ISO 639-1 code, e.g.
  // "en"/"ar"). Written by ProfileSetupPage.tsx (signup) and
  // ProfilePage.tsx (later change); read by LanguageContext.tsx, which
  // applies it to i18next/RTL for the learner UI only — never read by
  // anything admin-side. Defaults to English (undefined here means "use
  // the default", not "unset the language").
  preferredLanguage?: string;
  /** Round 139 - server-set while a deletion request is open (accountPurge.service.ts); PendingDeletionScreen shows "Keep my account". */
  deletion?: { requestedAt?: string; purgeAfter?: string; by?: 'learner' | 'inactivity' | 'admin' };
}

// CO3 — bare existence, not shape: VerifiedRoute only needs to know whether
// subscriptions/{uid} exists at all (subscription-not-yet-activated is a
// real, reachable state — TrialActivationPage's "Start your trial" is a
// separate, later step after placement, not implied by it).
export interface SubscriptionRecord {
  status?: string;
  planType?: string;
  planId?: string;
  planEndDate?: string | null;
  trialLessonsTotal?: number;
  trialLessonsUsed?: number;
  // Round 124 - the rest of what the plan card shows (all already on the subscriptions/{uid} document)
  startDate?: string;
  endDate?: string | null;
  planStartDate?: string;
  lessonsTotal?: number;
  lessonsUsed?: number;
  monthlyBaseLessonsTotal?: number;
  monthlyBaseLessonsUsed?: number;
  rolloverLessonsTotal?: number;
  rolloverLessonsUsed?: number;
  blockPurchasedAt?: string;
  blockExpiresAt?: string;
  /** Round 128 - the block attempt / level a block purchase covers */
  blockAttempt?: number;
  blockLevel?: string;
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  profile: LearnerProfile | null;
  subscription: SubscriptionRecord | null;
  loginWithEmail: (email: string, password: string) => Promise<LearnerProfile | null>;
  /** Round 141 - the server creates the account and e-mails the confirmation link; this then signs in with the password. */
  registerWithEmail: (email: string, password: string) => Promise<LearnerProfile | null>;
  loginWithGoogle: () => Promise<LearnerProfile | null>;
  /** Round 117b - finishes a sign-in that stopped at `auth/multi-factor-auth-required`, using the authenticator-app code. */
  completeMfaSignIn: (resolver: MultiFactorResolver, code: string) => Promise<LearnerProfile | null>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<EmailConfirmationOutcome>;
  /** Round 141 - re-reads the account and refreshes the ID token (Firestore's rules read email_verified from it); true once the address is confirmed. */
  reloadUser: () => Promise<boolean>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = AuthContextRef;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionRecord | null>(null);
  // Round 141 - reload() changes the SAME User object (emailVerified, email); a counter makes the provider re-render so every page sees it
  const [, setUserVersion] = useState(0);

  async function loadProfile(uid: string): Promise<LearnerProfile | null> {
    try {
      const snap = await getDoc(doc(db, 'learnerProfiles', uid));
      if (snap.exists()) {
        const p: LearnerProfile = { uid, ...(snap.data() as Omit<LearnerProfile, 'uid'>) };
        setProfile(p);
        return p;
      }
    } catch (e) {
      console.warn('[AuthContext] loadProfile failed', e);
    }
    return null;
  }

  // CO3 — mirrors loadProfile's own shape/error handling exactly: same
  // resolve-before-loading-flips-false timing VerifiedRoute depends on so
  // it never reads a stale null mid-fetch.
  async function loadSubscription(uid: string): Promise<SubscriptionRecord | null> {
    try {
      const snap = await getDoc(doc(db, 'subscriptions', uid));
      if (snap.exists()) {
        const s = snap.data() as SubscriptionRecord;
        setSubscription(s);
        return s;
      }
    } catch (e) {
      console.warn('[AuthContext] loadSubscription failed', e);
    }
    setSubscription(null);
    return null;
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // Round 132 (E): signed out -> no draft stays on the device; signed in -> drop any token an older build stored beside a draft.
      if (u) scrubStoredAuthTokens(); else clearAllLocalDrafts();
      // Round 79, Part B — re-arm `loading` on EVERY auth transition, not
      // just the very first mount. Before this, `loading` only started
      // `true` (useState(true) above) and was set false once, here, after
      // the FIRST onAuthStateChanged firing — so for the rest of the
      // session's life it stayed `false`, including during a fresh sign-in
      // transition. VerifiedRoute (App.tsx) checks `loading` FIRST, before
      // `user`/`profile`/`subscription`, specifically so it never redirects
      // on partially-loaded state — but that guard only worked on the
      // initial mount. A learner who signs in from a page that was already
      // mounted (SignInPage/SignUpPage/WelcomePage, `loading` already
      // false) would have `user` set here synchronously while
      // `loadSubscription` (and, for loginWithEmail/loginWithGoogle's own
      // separate direct profile fetch, `loadProfile`) was still in flight —
      // VerifiedRoute would see `subscription === null` and read that as
      // "no subscription exists," bouncing to /onboarding/trial for exactly
      // one render, even for a learner who already has one. Setting
      // `loading` true again here, before the fetch starts, makes every
      // consumer show FullPageSpinner through this transition too, exactly
      // like the first mount already did.
      //
      // Round 79/80 — the race itself is confirmed by reading the code above
      // (loading was only ever armed on the first onAuthStateChanged firing),
      // and the fix is narrow (one flag, re-armed on every firing, all 3 real
      // consumers are route guards that already wanted this exact behavior).
      // A live before/after reproduction was attempted and did NOT force the
      // symptom in that session — SignInPage's own resolvePostAuthRoute does
      // its own sequential Firestore reads before navigating, which happened
      // to give the background fetch enough time to settle regardless of
      // this fix. Do not read this comment as claiming a captured live
      // repro; see AuthContext.test.tsx's "re-arms loading on a later
      // onAuthStateChanged firing" test for what actually proves the
      // mechanism.
      setLoading(true);
      setUser(u);
      if (u) {
        await Promise.all([loadProfile(u.uid), loadSubscription(u.uid)]);
      } else {
        setProfile(null);
        setSubscription(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Round 117c - the subscription (and the profile copies of it) were read ONCE, when the learner signed in, and never again: an
  // admin grant / edit, a SlickPay or Stripe activation, a revoke or an expiry reached the database at once but a learner whose
  // tab was already open kept seeing the old plan (a granted monthly account still read TRIAL) until they signed out and in. Now
  // they are re-read whenever the tab becomes visible or the window regains focus (at most every 15 s), and pages that show
  // entitlement also call refreshProfile() on mount.
  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    let last = Date.now();
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (Date.now() - last < 15_000) return;
      last = Date.now();
      void Promise.all([loadProfile(uid), loadSubscription(uid)]).catch(() => undefined);
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [user?.uid]);

  async function loginWithEmail(email: string, password: string): Promise<LearnerProfile | null> {
    const { user: u } = await signInWithEmailAndPassword(auth, email, password);
    return loadProfile(u.uid);
  }

  async function registerWithEmail(email: string, password: string): Promise<LearnerProfile | null> {
    await signUpWithEmail({ email, password, lang: i18n.language ?? 'en' });
    const { user: u } = await signInWithEmailAndPassword(auth, email.trim(), password);
    return loadProfile(u.uid);
  }

  async function loginWithGoogle(): Promise<LearnerProfile | null> {
    const { user: u } = await signInWithPopup(auth, new GoogleAuthProvider());
    return loadProfile(u.uid);
  }

  async function completeMfaSignIn(resolver: MultiFactorResolver, code: string): Promise<LearnerProfile | null> {
    const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) throw Object.assign(new Error('No authenticator-app factor on this account'), { code: 'emi/no-totp-factor' });
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
    const cred = await resolver.resolveSignIn(assertion);
    return loadProfile(cred.user.uid);
  }

  async function logout() {
    await signOut(auth);
    setProfile(null);
    setSubscription(null);
  }

  async function sendVerificationEmail(): Promise<EmailConfirmationOutcome> {
    return sendEmailConfirmation(i18n.language ?? 'en');
  }

  async function reloadUser(): Promise<boolean> {
    const u = auth.currentUser;
    if (!u) return false;
    await reload(u);
    if (u.emailVerified) await u.getIdToken(true);
    setUserVersion((n) => n + 1);
    return u.emailVerified;
  }

  // CO3 — refreshes subscription alongside profile, not just profile. This
  // is the exact call TrialActivationPage makes right after activateTrial()
  // succeeds; VerifiedRoute's new subscription gate would otherwise still
  // see the pre-activation null and bounce the learner straight back to
  // /onboarding/trial after they just completed it.
  async function refreshProfile() {
    if (user) await Promise.all([loadProfile(user.uid), loadSubscription(user.uid)]);
  }

  return (
    <AuthContext.Provider value={{
      user, loading, profile, subscription,
      loginWithEmail, registerWithEmail, loginWithGoogle, completeMfaSignIn,
      logout, sendVerificationEmail, reloadUser, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
